import { VisualRandom } from '../core/VisualRandom.js';

/**
 * The colours a pigment material paints with.
 *
 * ## Why this exists, and what was actually wrong
 *
 * INK_PAINT and WATERCOLOR both read as white or near-grey, and the cause was
 * measured rather than guessed. Three things were true and only two of them
 * were faults:
 *
 * 1. **INK_PAINT had no palette at all.** Its default pigment was
 *    `[0.19, 0.20, 0.24]` rising to `[0.30, 0.31, 0.36]` — a near-neutral
 *    blue-grey with a saturation around 0.1. It was grey because it was
 *    written grey, not because anything took the colour away.
 * 2. **WATERCOLOR's hue was locked to a narrow band.** It had hue, but centred
 *    on 0.55 with a spread of 0.05–0.18, so every wash in every run was some
 *    shade of cyan.
 * 3. **The pipeline desaturates bright colour.** This one is not a fault; it is
 *    a constraint, and it decides how the other two must be fixed.
 *
 * ## The constraint, measured
 *
 * The stage renders through ACES filmic tone mapping with bloom over it. Quads
 * of known colour pushed through the real pipeline and read back give:
 *
 * ```
 * input luminance   0.15   0.30   0.45   0.60   0.85   1.00
 * red      sat      0.99   0.99   0.96   0.94   0.91   0.89
 * blue     sat      0.78   0.70   0.58   0.40   0.24   0.22
 * yellow   sat      1.00   0.93   0.80   0.66   0.23   0.19
 * green    sat      0.86   0.63   0.49   0.42   0.30   0.08
 * magenta  sat      0.75   0.69   0.61   0.51   0.37   0.28
 * ```
 *
 * Saturation survives below about 0.3 and falls off a cliff above 0.45 — green
 * at full input comes back as 0.08, which is white with a hint of green. So the
 * way to get a vivid red on screen is **not** to write a brighter red. It is to
 * write a *dark* saturated one and let the tone curve lift it: an input of 0.15
 * already comes back at luminance 22–98 depending on hue, which is plainly
 * visible.
 *
 * Every colour below is therefore authored in the 0.12–0.40 range. They look
 * wrong in a swatch and correct on screen, which is the whole point of having
 * measured first.
 */

export type Rgb = readonly [number, number, number];

export interface VisualPalette {
  readonly id: string;
  readonly colors: readonly Rgb[];
  /** Multiplies the distance from grey. Above 1 pushes towards the primaries. */
  readonly saturationScale?: number;
  /** Multiplies value. Kept at or below 1 for anything meant to stay saturated. */
  readonly brightnessScale?: number;
  /** How strongly a mark takes on the colour already under it, 0..1. */
  readonly backgroundInfluence?: number;
}

export const PALETTE_IDS = ['PRIMARY', 'NEON', 'WARM', 'COOL', 'RANDOM_PIGMENT'] as const;
export type PaletteId = (typeof PALETTE_IDS)[number];

export function isPaletteId(value: string): value is PaletteId {
  return (PALETTE_IDS as readonly string[]).includes(value);
}

/**
 * The fixed palettes.
 *
 * Five or six colours each, deliberately. A palette with twenty entries stops
 * being a palette — the run stops having a colour identity and becomes noise,
 * and no viewer can tell one seed's artwork from another's.
 */
const FIXED: Readonly<Record<Exclude<PaletteId, 'RANDOM_PIGMENT'>, VisualPalette>> = {
  PRIMARY: {
    id: 'PRIMARY',
    colors: [
      [0.34, 0.03, 0.04],   // red
      [0.04, 0.09, 0.36],   // blue
      [0.32, 0.26, 0.02],   // yellow
      [0.04, 0.26, 0.07],   // green
      [0.30, 0.03, 0.22],   // magenta
    ],
    saturationScale: 1,
    brightnessScale: 1,
    backgroundInfluence: 0.35,
  },
  NEON: {
    id: 'NEON',
    colors: [
      [0.03, 0.30, 0.33],   // cyan
      [0.32, 0.03, 0.26],   // magenta
      [0.05, 0.14, 0.38],   // electric blue
      [0.16, 0.32, 0.03],   // acid green
      [0.36, 0.05, 0.08],   // hot red
    ],
    // Neon is the one palette that earns a push past the primaries: its
    // identity is the edge of the gamut rather than the pigment box.
    saturationScale: 1.15,
    brightnessScale: 1.05,
    backgroundInfluence: 0.28,
  },
  WARM: {
    id: 'WARM',
    colors: [
      [0.33, 0.03, 0.08],   // crimson
      [0.34, 0.14, 0.02],   // orange
      [0.32, 0.26, 0.02],   // yellow
      [0.24, 0.02, 0.03],   // deep red
      [0.30, 0.08, 0.04],   // vermilion
    ],
    saturationScale: 1,
    brightnessScale: 1,
    backgroundInfluence: 0.4,
  },
  COOL: {
    id: 'COOL',
    colors: [
      [0.03, 0.10, 0.36],   // cobalt
      [0.03, 0.28, 0.32],   // cyan
      [0.16, 0.04, 0.34],   // violet
      [0.02, 0.26, 0.17],   // emerald
      [0.06, 0.18, 0.30],   // steel blue
    ],
    saturationScale: 1,
    brightnessScale: 1,
    backgroundInfluence: 0.4,
  },
};

/**
 * The palette an absent setting means.
 *
 * PRIMARY rather than "whatever it used to be". The grey that INK_PAINT painted
 * before was not a design choice anybody made — it was the absence of one — so
 * there is nothing to preserve compatibility *with*. A recipe that says nothing
 * about colour gets the palette that most obviously reads as paint.
 */
export const DEFAULT_PALETTE_ID: PaletteId = 'PRIMARY';

/**
 * Builds a palette from a seed.
 *
 * Five hues spread evenly around the wheel from a seeded starting point, so a
 * generated palette is still a *palette* — related colours at a consistent
 * strength — rather than five unrelated draws. The spacing is jittered a little
 * so two seeds do not produce rotations of one set.
 */
function randomPigment(seed: number): VisualPalette {
  const random = new VisualRandom(seed ^ 0x9e3779b9);
  const start = random.next();
  const colors: Rgb[] = [];
  for (let index = 0; index < 5; index += 1) {
    const hue = (start + index / 5 + random.signed() * 0.05 + 1) % 1;
    // Saturation stays high and value stays low, for the reason at the top of
    // this file: a bright generated colour comes back off the tone curve white.
    const saturation = 0.82 + random.next() * 0.16;
    const value = 0.24 + random.next() * 0.14;
    colors.push(hsvToRgb(hue, saturation, value));
  }
  return {
    id: 'RANDOM_PIGMENT',
    colors,
    saturationScale: 1,
    brightnessScale: 1,
    backgroundInfluence: 0.35,
  };
}

/**
 * The palette for an id, with generated ones built from the seed.
 *
 * Seeded rather than random, which is the whole of §12: the same recipe and
 * seed must produce the same colours, and `RANDOM_PIGMENT` would otherwise be
 * the one thing in the pipeline that could not be reproduced.
 */
export function resolvePalette(id: string | undefined, seed: number): VisualPalette {
  if (id === undefined) return FIXED[DEFAULT_PALETTE_ID as Exclude<PaletteId, 'RANDOM_PIGMENT'>];
  if (id === 'RANDOM_PIGMENT') return randomPigment(seed);
  if (isPaletteId(id) && id !== 'RANDOM_PIGMENT') return FIXED[id];
  return FIXED[DEFAULT_PALETTE_ID as Exclude<PaletteId, 'RANDOM_PIGMENT'>];
}

/**
 * One colour from a palette, by index.
 *
 * Wraps rather than clamps, so a caller can hand it a counter and get a cycle.
 * The scales are applied here rather than baked into the table so that a
 * palette can be reused at a different strength without a second table.
 */
export function paletteColor(palette: VisualPalette, index: number): Rgb {
  const colors = palette.colors;
  if (colors.length === 0) return [0.3, 0.3, 0.3];
  const base = colors[((index % colors.length) + colors.length) % colors.length] as Rgb;
  const saturation = palette.saturationScale ?? 1;
  const brightness = palette.brightnessScale ?? 1;
  if (saturation === 1 && brightness === 1) return base;

  // Saturation is scaled about the colour's own grey point, so pushing it does
  // not change which hue it is.
  const grey = (base[0] + base[1] + base[2]) / 3;
  return [
    clamp01((grey + (base[0] - grey) * saturation) * brightness),
    clamp01((grey + (base[1] - grey) * saturation) * brightness),
    clamp01((grey + (base[2] - grey) * saturation) * brightness),
  ];
}

/**
 * Mixes two pigments the way overlapping paint looks.
 *
 * Not a linear average, which is what makes red over blue come out a muddy
 * grey-violet instead of purple. Each channel is combined towards the *larger*
 * of the two by a fraction of the smaller, which keeps the result inside the
 * gamut of the two pigments while letting the shared channel dominate — so red
 * plus blue reads purple, blue plus yellow reads green, and red plus yellow
 * reads orange, which is what §6 asks to be visible.
 *
 * Deliberately not a fluid model. The brief rules that out, and an approximation
 * that lands in the right hue is worth more here than a simulation that is
 * correct and invisible.
 */
export function mixPigments(a: Rgb, b: Rgb, amount: number): Rgb {
  const t = clamp01(amount);
  const out: [number, number, number] = [0, 0, 0];
  for (let channel = 0; channel < 3; channel += 1) {
    const first = a[channel] as number;
    const second = b[channel] as number;
    const high = Math.max(first, second);
    const low = Math.min(first, second);
    // At t = 0 this is `a`; at t = 1 it is the pair's upper envelope pulled
    // partway down by the weaker channel, which is where the third hue lives.
    const target = high * 0.82 + low * 0.5;
    out[channel] = clamp01(first + (target - first) * t);
  }
  return out;
}

/** Saturation as a fraction, for the colour measurements the phase asks for. */
export function saturationOf(colour: Rgb): number {
  const max = Math.max(colour[0], colour[1], colour[2]);
  const min = Math.min(colour[0], colour[1], colour[2]);
  return max <= 0 ? 0 : (max - min) / max;
}

function hsvToRgb(h: number, s: number, v: number): Rgb {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: return [v, t, p];
    case 1: return [q, v, p];
    case 2: return [p, v, t];
    case 3: return [p, q, v];
    case 4: return [t, p, v];
    default: return [v, p, q];
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
